// The signed-in employee's face check-in status (GET /attendance/face/status),
// gated by the client's biometric switch: with the switch off nothing is asked
// and the feature reads as OFF.
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { faceApi, FACE_MODES } from "./faceApi";

const OFF = { mode: FACE_MODES.OFF, consented: false, enrolled: false, modelVersionOk: true, serviceAvailable: true, available: false };
const OUTAGE_POLL_MS = 60 * 1000; // re-check while the face service is down

export function useFaceStatus({ enabled = true } = {}) {
  const { isClientModuleEnabled, user } = useAuth();
  const switchOn = Boolean(user) && isClientModuleEnabled("faceCheckIn") && isClientModuleEnabled("attendance");
  const [state, setState] = useState({ loading: enabled && switchOn, status: OFF, error: null });

  const reload = useCallback(async () => {
    if (!enabled || !switchOn) { setState({ loading: false, status: OFF, error: null }); return OFF; }
    setState((s) => ({ ...s, loading: true }));
    try {
      const status = await faceApi.status();
      setState({ loading: false, status, error: null });
      return status;
    } catch (err) {
      // Unknown → behave as before the feature (regular punch), but remember why.
      setState({ loading: false, status: OFF, error: err });
      return OFF;
    }
  }, [enabled, switchOn]);

  useEffect(() => { reload(); }, [reload]);

  const { status } = state;
  // Face service down: poll until /status says it is back, then face resumes.
  const serviceDown = Boolean(status.available) && status.serviceAvailable === false && status.mode !== FACE_MODES.OFF;
  useEffect(() => {
    if (!serviceDown) return undefined;
    const id = setInterval(() => { reload(); }, OUTAGE_POLL_MS);
    return () => clearInterval(id);
  }, [serviceDown, reload]);
  return {
    ...state,
    reload,
    switchOn,
    mode: status.mode,
    isOff: status.mode === FACE_MODES.OFF,
    isRequired: status.mode === FACE_MODES.REQUIRED,
    isOptional: status.mode === FACE_MODES.OPTIONAL,
    /** The face service is unreachable: the regular punch is in use (server-flagged). */
    serviceDown,
    /** Enrolled with a template the current model accepts (and the service is up). */
    canUseFace: !serviceDown && status.mode !== FACE_MODES.OFF && status.enrolled && status.modelVersionOk,
    needsEnrolment: !serviceDown && status.mode !== FACE_MODES.OFF && (!status.enrolled || !status.modelVersionOk),
    setStatus: (patch) => setState((s) => ({ ...s, status: { ...s.status, ...patch } })),
  };
}

export default useFaceStatus;
